import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Box,
  Typography,
  CircularProgress,
  TextField,
  InputAdornment,
} from "@mui/material";
import { Search } from "@mui/icons-material";

export interface TableColumn<T = any> {
  key: string;
  label: string;
  render?: (value: any, row: T) => React.ReactNode;
  align?: "left" | "center" | "right";
  width?: string | number;
}

export interface DataTableProps<T = any> {
  data: T[];
  columns: TableColumn<T>[];
  isLoading?: boolean;
  emptyStateIcon?: React.ReactNode;
  emptyStateTitle?: string;
  emptyStateSubtitle?: string;
  onRowClick?: (row: T) => void;
  sx?: any;
  // Pagination props
  enablePagination?: boolean;
  initialPage?: number;
  initialRowsPerPage?: number;
  rowsPerPageOptions?: number[];
  onPaginationChange?: (page: number, rowsPerPage: number) => void;
  // Search props
  enableSearch?: boolean;
  searchPlaceholder?: string;
  searchableColumns?: string[];
  // Row styling prop
  getRowSx?: (row: T, index: number) => any;
  // Empty state with table structure
  showTableOnEmpty?: boolean;
}

const DataTable = <T extends Record<string, any>>({
  data,
  columns,
  isLoading,
  emptyStateIcon,
  emptyStateTitle = "No data found",
  emptyStateSubtitle = "Data will appear here when available",
  onRowClick,
  sx,
  enablePagination = false,
  initialPage = 0,
  initialRowsPerPage = 10,
  rowsPerPageOptions = [5, 10, 25, 50],
  onPaginationChange,
  enableSearch = false,
  searchPlaceholder = "Search...",
  searchableColumns,
  getRowSx,
  showTableOnEmpty = false,
}: DataTableProps<T>) => {
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [searchQuery, setSearchQuery] = useState("");

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    onPaginationChange?.(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    onPaginationChange?.(0, newRowsPerPage);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  // Filter data based on search query
  const filteredData = useMemo(() => {
    if (!enableSearch || !searchQuery.trim()) {
      return data;
    }

    const searchLower = searchQuery.toLowerCase();
    console.log("DataTable: Filtering data with search query:", searchQuery);
    console.log("DataTable: Searchable columns:", searchableColumns);
    console.log("DataTable: Sample data item:", data[0]);

    const filtered = data.filter((row) => {
      // If specific searchable columns are defined, only search in those
      if (searchableColumns && searchableColumns.length > 0) {
        const match = searchableColumns.some((columnKey) => {
          const value = String(row[columnKey] || "").toLowerCase();
          const includes = value.includes(searchLower);
          if (includes) {
            console.log(
              `DataTable: Found match in column ${columnKey}: "${value}" contains "${searchLower}"`
            );
          }
          return includes;
        });
        return match;
      }

      // Otherwise, search in all columns
      return columns.some((column) => {
        const value = row[column.key];
        return String(value || "")
          .toLowerCase()
          .includes(searchLower);
      });
    });

    console.log("DataTable: Filtered results count:", filtered.length);
    return filtered;
  }, [data, searchQuery, enableSearch, searchableColumns, columns]);

  // Calculate paginated data from filtered data
  const paginatedData = enablePagination
    ? filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : filteredData;
  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 6,
          borderRadius: 2,
          backgroundColor: "grey.50",
          ...sx,
        }}
      >
        <CircularProgress
          size={48}
          thickness={4}
          sx={{ color: "primary.main" }}
        />
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            mt: 3,
            fontWeight: 500,
            fontSize: "0.9375rem",
          }}
        >
          Loading data...
        </Typography>
      </Box>
    );
  }

  if (!data || data.length === 0) {
    // If showTableOnEmpty is enabled, show only table headers without data rows
    if (showTableOnEmpty) {
      return (
        <Box sx={sx}>
          {enableSearch && (
            <TextField
              fullWidth
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={handleSearchChange}
              sx={{
                mb: 3,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "grey.50",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
            />
          )}
          <TableContainer
            sx={{
              border: "none",
              boxShadow: "none",
              borderRadius: 2,
              overflowX: { xs: "auto", sm: "auto" },
              overflowY: "hidden",
            }}
          >
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow
                  sx={{
                    background:
                      "linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)",
                    "& th": {
                      borderBottom: "2px solid",
                      borderColor: "primary.main",
                      py: 2.5,
                      px: 3,
                    },
                  }}
                >
                  {columns.map((column) => (
                    <TableCell
                      key={column.key}
                      align={column.align || "left"}
                      sx={{
                        width: column.width,
                        color: "primary.main",
                        fontWeight: 700,
                        fontSize: "0.8125rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.8px",
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>{/* Empty body - no rows */}</TableBody>
            </Table>
          </TableContainer>
          <Box
            sx={{
              mt: 2,
              p: 3,
              textAlign: "center",
              borderRadius: 2,
              backgroundColor: "rgba(102, 126, 234, 0.04)",
              border: "1px dashed",
              borderColor: "primary.main",
            }}
          >
            {emptyStateIcon && (
              <Box sx={{ mb: 2, opacity: 0.5, color: "primary.main" }}>
                {emptyStateIcon}
              </Box>
            )}
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{
                fontWeight: 600,
                color: "text.primary",
              }}
            >
              {emptyStateTitle}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontSize: "0.875rem",
              }}
            >
              {emptyStateSubtitle}
            </Typography>
          </Box>
        </Box>
      );
    }

    // Original empty state without table
    return (
      <Box sx={sx}>
        {enableSearch && (
          <TextField
            fullWidth
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{
              mb: 3,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: "grey.50",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
          />
        )}
        <Box
          sx={{
            p: 6,
            textAlign: "center",
            borderRadius: 2,
            backgroundColor: "grey.50",
            border: "2px dashed",
            borderColor: "divider",
          }}
        >
          {emptyStateIcon && (
            <Box sx={{ mb: 3, opacity: 0.4, color: "text.secondary" }}>
              {emptyStateIcon}
            </Box>
          )}
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            {emptyStateTitle}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontSize: "0.9375rem",
            }}
          >
            {emptyStateSubtitle}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Show "no results" message when search returns no results
  if (enableSearch && searchQuery.trim() && filteredData.length === 0) {
    return (
      <Box sx={sx}>
        <TextField
          fullWidth
          placeholder={searchPlaceholder}
          value={searchQuery}
          onChange={handleSearchChange}
          sx={{
            mb: 3,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              backgroundColor: "grey.50",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
        />
        <Box
          sx={{
            p: 6,
            textAlign: "center",
            borderRadius: 2,
            backgroundColor: "grey.50",
            border: "2px dashed",
            borderColor: "divider",
          }}
        >
          <Search sx={{ fontSize: 56, color: "text.disabled", mb: 2 }} />
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            No results found
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontSize: "0.9375rem",
            }}
          >
            Try adjusting your search terms
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={sx}>
      {enableSearch && (
        <TextField
          fullWidth
          placeholder={searchPlaceholder}
          value={searchQuery}
          onChange={handleSearchChange}
          sx={{
            mb: 3,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              backgroundColor: "grey.50",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "background.paper",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "primary.main",
                },
              },
              "&.Mui-focused": {
                backgroundColor: "background.paper",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderWidth: 2,
                  borderColor: "primary.main",
                },
              },
            },
            "& input": {
              py: 1.5,
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: "text.secondary", fontSize: "1.25rem" }} />
              </InputAdornment>
            ),
          }}
        />
      )}
      <TableContainer
        sx={{
          border: "none",
          boxShadow: "none",
          borderRadius: 2,
          overflowX: { xs: "auto", sm: "auto" },
          overflowY: "hidden",
        }}
      >
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow
              sx={{
                background:
                  "linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)",
                "& th": {
                  borderBottom: "2px solid",
                  borderColor: "primary.main",
                  py: 2.5,
                  px: 3,
                },
              }}
            >
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.align || "left"}
                  sx={{
                    width: column.width,
                    color: "primary.main",
                    fontWeight: 700,
                    fontSize: "0.8125rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.8px",
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row, index) => (
              <TableRow
                key={row.id || index}
                sx={{
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: onRowClick ? "pointer" : "default",
                  "&:hover": {
                    backgroundColor: "rgba(102, 126, 234, 0.04)",
                    transform: onRowClick ? "translateX(4px)" : "none",
                    boxShadow: onRowClick
                      ? "0 2px 8px rgba(0,0,0,0.08)"
                      : "none",
                  },
                  "& td": {
                    borderBottom: "1px solid",
                    borderColor: "grey.200",
                    py: 2.5,
                    px: 3,
                    fontSize: "0.9375rem",
                    transition: "all 0.2s ease",
                  },
                  "& td:first-of-type": {
                    borderLeft: "3px solid transparent",
                    transition: "all 0.2s ease",
                    fontWeight: 500,
                  },
                  "&:hover td:first-of-type": {
                    borderLeftColor: "primary.main",
                  },
                  "&:last-child td": {
                    borderBottom: "none",
                  },
                  ...getRowSx?.(row, index),
                }}
                onClick={() => onRowClick?.(row)}
              >
                {columns.map((column) => (
                  <TableCell key={column.key} align={column.align || "left"}>
                    {column.render
                      ? column.render(row[column.key], row)
                      : row[column.key]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {enablePagination && (
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            mt: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            "& .MuiTablePagination-toolbar": {
              py: 2,
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                fontWeight: 500,
                fontSize: "0.875rem",
              },
            "& .MuiIconButton-root": {
              borderRadius: 2,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "primary.50",
                color: "primary.main",
              },
            },
          }}
        />
      )}
    </Box>
  );
};

export default DataTable;
